import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";

import Layout from "./components/Layout";
import HeroSection from "./components/emi/HeroSection";
import FeatureHighlights from "./components/emi/FeatureHighlights";
import LoanInputPanel from "./components/emi/LoanInputPanel";
import ChartSection from "./components/emi/ChartSection";
import ActionButtons from "./components/emi/ActionButtons";
import DTICalculator from "./components/emi/DTICalculator";

// URL helpers
function getNumber(qs, key, fallback) {
  const v = Number(qs.get(key));
  return Number.isFinite(v) && v >= 0 ? v : fallback;
}

function setQueryParams(params) {
  if (typeof window === "undefined") return;
  const qs = new URLSearchParams(params);
  const url = `${window.location.pathname}?${qs.toString()}${window.location.hash || ""}`;
  window.history.replaceState({}, "", url);
}

async function copyToClipboard(text) {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) {}
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.left = "-999999px";
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand("copy");
    return true;
  } catch {
    return false;
  } finally {
    document.body.removeChild(ta);
  }
}

function downloadScheduleCSV(schedule) {
  if (!schedule?.length) return;
  const header = ["Month", "Principal", "Interest", "EMI", "Balance"];
  const rows = schedule.map((r) => [r.month, r.principal, r.interest, r.emi, r.balance]);
  const csv = [header, ...rows].map((r) => r.join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `paisa-emi-schedule-${new Date().toISOString().split("T")[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// EMI calculation hook with both Flat and Reducing methods
function useEMI({ principal, annualRate, tenureMonths, isFlat = false }) {
  const P = Number(principal) || 0;
  const n = Number(tenureMonths) || 0;
  const annualRateNum = Number(annualRate) || 0;

  return useMemo(() => {
    if (!P || !n || annualRateNum <= 0) {
      return { emi: 0, totalInterest: 0, totalPayment: 0, schedule: [], effectiveRate: 0 };
    }

    let emi, totalInterest, totalPayment, schedule, effectiveRate;

    if (isFlat) {
      // FLAT RATE
      totalInterest = P * (annualRateNum / 100) * (n / 12);
      totalPayment = P + totalInterest;
      emi = totalPayment / n;

      schedule = [];
      let remainingPrincipal = P;
      const monthlyPrincipal = P / n;
      const monthlyInterest = totalInterest / n;

      for (let i = 1; i <= n; i++) {
        remainingPrincipal -= monthlyPrincipal;
        schedule.push({
          month: i,
          interest: Number(monthlyInterest.toFixed(2)),
          principal: Number(monthlyPrincipal.toFixed(2)),
          balance: Number(Math.max(0, remainingPrincipal).toFixed(2)),
          emi: Number(emi.toFixed(2)),
        });
      }

      // Rough equivalent reducing rate
      effectiveRate = annualRateNum * 1.85;
    } else {
      // REDUCING BALANCE
      const r = annualRateNum / 12 / 100;
      emi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);

      let balance = P;
      totalInterest = 0;
      schedule = [];

      for (let i = 1; i <= n; i++) {
        const interestForMonth = balance * r;
        const principalForMonth = emi - interestForMonth;
        balance = balance - principalForMonth;

        if (i === n && Math.abs(balance) < 1) balance = 0;

        totalInterest += interestForMonth;

        schedule.push({
          month: i,
          interest: Number(interestForMonth.toFixed(2)),
          principal: Number(principalForMonth.toFixed(2)),
          balance: Number(Math.max(0, balance).toFixed(2)),
          emi: Number(emi.toFixed(2)),
        });
      }

      totalPayment = P + totalInterest;
      effectiveRate = annualRateNum;
    }

    return {
      emi: Number(emi.toFixed(2)),
      totalInterest: Number(totalInterest.toFixed(2)),
      totalPayment: Number(totalPayment.toFixed(2)),
      schedule,
      effectiveRate: Number(effectiveRate.toFixed(2)),
    };
  }, [P, annualRateNum, n, isFlat]);
}

// Currency formatting components
function NumberIN({ value }) {
  return <>{new Intl.NumberFormat("en-IN").format(value || 0)}</>;
}

// EMI Table component
function EMITable({ schedule }) {
  if (!schedule?.length) return null;
  return (
    <div className="overflow-auto max-h-80 rounded-xl ring-1 ring-neutral-200">
      <table className="min-w-full text-sm">
        <thead className="bg-neutral-50 sticky top-0 z-10">
          <tr>
            <th className="text-left p-3 font-medium">Month</th>
            <th className="text-right p-3 font-medium">Principal</th>
            <th className="text-right p-3 font-medium">Interest</th>
            <th className="text-right p-3 font-medium">EMI</th>
            <th className="text-right p-3 font-medium">Balance</th>
          </tr>
        </thead>
        <tbody>
          {schedule.map((row) => (
            <tr key={row.month} className="odd:bg-white even:bg-neutral-50 hover:bg-neutral-100 transition-colors">
              <td className="p-3">{row.month}</td>
              <td className="p-3 text-right"><NumberIN value={row.principal} /></td>
              <td className="p-3 text-right"><NumberIN value={row.interest} /></td>
              <td className="p-3 text-right"><NumberIN value={row.emi} /></td>
              <td className="p-3 text-right"><NumberIN value={row.balance} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const formatCurrency = (amount) => {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
  if (amount >= 1000) return `₹${(amount / 1000).toFixed(0)}K`;
  return `₹${Math.round(amount).toLocaleString("en-IN")}`;
};

const formatTooltipCurrency = (amount) => `₹${Math.round(amount).toLocaleString("en-IN")}`;

function EMICalculator() {
  const qs = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const [principal, setPrincipal] = useState(getNumber(qs, "amount", 2500000));
  const [rate, setRate] = useState(getNumber(qs, "rate", 8.5));
  const [tenure, setTenure] = useState(getNumber(qs, "years", 20));
  const [currentMonth, setCurrentMonth] = useState(1);
  const [isFlat, setIsFlat] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [copied, setCopied] = useState(false);

  const tenureMonths = tenure * 12;
  const { emi, totalInterest, totalPayment, schedule, effectiveRate } = useEMI({
    principal,
    annualRate: rate,
    tenureMonths,
    isFlat,
  });

  const chartData = useMemo(() => {
    if (!schedule?.length) return [];
    const yearlyData = [];
    for (let i = 11; i < schedule.length; i += 12) {
      const item = schedule[i];
      if (item) {
        const cumulativeInterest = schedule.slice(0, i + 1).reduce((sum, m) => sum + m.interest, 0);
        yearlyData.push({
          year: Math.ceil(item.month / 12),
          "Outstanding Principal": item.balance,
          "Principal Paid": principal - item.balance,
          "Interest Paid": cumulativeInterest,
        });
      }
    }
    const lastItem = schedule[schedule.length - 1];
    if (lastItem && yearlyData[yearlyData.length - 1]?.year !== Math.ceil(lastItem.month / 12)) {
      const ti = schedule.reduce((sum, m) => sum + m.interest, 0);
      yearlyData.push({
        year: Math.ceil(lastItem.month / 12),
        "Outstanding Principal": 0,
        "Principal Paid": principal,
        "Interest Paid": ti,
      });
    }
    return yearlyData;
  }, [schedule, principal]);

  const currentPayment = schedule[currentMonth - 1];
  const paidPrincipal = principal - (currentPayment?.balance || principal);
  const paidInterest = (currentMonth - 1) * emi - paidPrincipal;

  useEffect(() => {
    const timer = setTimeout(() => setQueryParams({ amount: principal, rate, years: tenure }), 500);
    return () => clearTimeout(timer);
  }, [principal, rate, tenure]);

  useEffect(() => setCurrentMonth(1), [principal, rate, tenure]);

  const handleShare = async () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    const ok = await copyToClipboard(url);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Layout>
      <HeroSection />
      <FeatureHighlights />

      <div id="calculator" className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Input Panel */}
          <div className="lg:col-span-1">
            <LoanInputPanel
              principal={principal}
              setPrincipal={setPrincipal}
              rate={rate}
              setRate={setRate}
              tenure={tenure}
              setTenure={setTenure}
              currentMonth={currentMonth}
              setCurrentMonth={setCurrentMonth}
              isFlat={isFlat}
              setIsFlat={setIsFlat}
              effectiveRate={effectiveRate}
              formatCurrency={formatCurrency}
            />

            <div className="mt-8">
              <ActionButtons
                showTable={showTable}
                setShowTable={setShowTable}
                schedule={schedule}
                downloadScheduleCSV={downloadScheduleCSV}
                handleShare={handleShare}
                copied={copied}
              />
            </div>
          </div>

          {/* Results Panel */}
          <div className="lg:col-span-2 space-y-8">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-neutral-600">Monthly EMI</h3>
                  <div className="h-8 w-8 rounded-lg bg-brand-primary/10 grid place-items-center">
                    <span className="text-brand-primary font-bold">₹</span>
                  </div>
                </div>
                <p className="text-2xl font-bold text-brand-primary">{formatCurrency(emi)}</p>
                <p className="text-xs text-neutral-500 mt-1">per month</p>
              </div>

              <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-neutral-600">Total Interest</h3>
                <div className="h-8 w-8 rounded-lg bg-brand-accent/10 grid place-items-center">
                    <span className="text-brand-accent font-bold">%</span>
                  </div>
                </div>
                <p className="text-2xl font-bold text-brand-accent">{formatCurrency(totalInterest)}</p>
                <p className="text-xs text-neutral-500 mt-1">over {tenure} years</p>
              </div>

              <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-neutral-600">Total Amount</h3>
                  <div className="h-8 w-8 rounded-lg bg-blue-100 grid place-items-center">
                    <span className="text-blue-600 font-bold">∑</span>
                  </div>
                </div>
                <p className="text-2xl font-bold text-blue-600">{formatCurrency(totalPayment)}</p>
                <p className="text-xs text-neutral-500 mt-1">total payable</p>
              </div>
            </div>

            <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-lg p-8">
              <h3 className="text-lg font-semibold mb-6 text-neutral-800 dark:text-neutral-100">
                Position at Month {currentMonth}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-sm text-neutral-600">Monthly EMI</span>
                    <span className="font-semibold">{formatTooltipCurrency(emi)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-neutral-600">Principal Component</span>
                    <span className="font-semibold text-brand-primary">
                      {formatTooltipCurrency(currentPayment?.principal || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-neutral-600">Interest Component</span>
                    <span className="font-semibold text-brand-accent">
                      {formatTooltipCurrency(currentPayment?.interest || 0)}
                    </span>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-sm text-neutral-600">Outstanding Balance</span>
                    <span className="font-semibold text-blue-600">
                      {formatTooltipCurrency(currentPayment?.balance || principal)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-neutral-600">Principal Paid So Far</span>
                    <span className="font-semibold text-brand-primary">
                      {formatTooltipCurrency(paidPrincipal)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-neutral-600">Interest Paid So Far</span>
                    <span className="font-semibold text-brand-accent">
                      {formatTooltipCurrency(Math.max(0, paidInterest))}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Chart Section */}
            <ChartSection
              chartData={chartData}
              formatCurrency={formatCurrency}
              formatTooltipCurrency={formatTooltipCurrency}
            />

            {/* Amortization Schedule Table */}
            {showTable && (
              <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-lg p-6">
                <h4 className="font-semibold mb-3 text-neutral-800 dark:text-neutral-100">
                  Amortization Schedule ({isFlat ? "Flat Rate" : "Reducing Balance"})
                </h4>
                <EMITable schedule={schedule} />
              </div>
            )}
          </div>
        </div>

        <div className="mt-8">
          <ActionButtons
            showTable={showTable}
            setShowTable={setShowTable}
            schedule={schedule}
            downloadScheduleCSV={downloadScheduleCSV}
            handleShare={handleShare}
            copied={copied}
          />
        </div>

        {/* DTI Calculator */}
        <div className="mt-16">
          <DTICalculator prospectiveEMI={emi} />
        </div>

        {/* Quick Tools */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-center mb-8 text-neutral-800 dark:text-neutral-100">
            Quick Financial Tools
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="group bg-white dark:bg-neutral-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 text-center">
              <div className="h-12 w-12 mx-auto mb-4 rounded-xl bg-gradient-to-br from-purple-500 to-purple-700 grid place-items-center">
                <span className="text-white text-xl">📊</span>
              </div>
              <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-100 mb-2">SIP Calculator</h3>
              <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-4">
                Plan your mutual fund investments with SIP calculations
              </p>
              <button className="text-purple-600 dark:text-purple-400 font-medium text-sm hover:text-purple-700 dark:hover:text-purple-500">
                Coming Soon →
              </button>
            </div>

            <div className="group bg-white dark:bg-neutral-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 text-center">
              <div className="h-12 w-12 mx-auto mb-4 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 grid place-items-center">
                <span className="text-white text-xl">💰</span>
              </div>
              <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-100 mb-2">FD Calculator</h3>
              <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-4">
                Calculate returns on your fixed deposits and recurring deposits
              </p>
              <button className="text-blue-600 dark:text-blue-400 font-medium text-sm hover:text-blue-700 dark:hover:text-blue-500">
                Coming Soon →
              </button>
            </div>

            <div className="group bg-white dark:bg-neutral-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 text-center">
              <div className="h-12 w-12 mx-auto mb-4 rounded-xl bg-gradient-to-br from-teal-500 to-teal-700 grid place-items-center">
                <span className="text-white text-xl">🏦</span>
              </div>
              <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-100 mb-2">Tax Calculator</h3>
              <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-4">
                Calculate income tax as per latest Indian tax slabs
              </p>
              <button className="text-teal-600 dark:text-teal-400 font-medium text-sm hover:text-teal-700 dark:hover:text-teal-500">
                Coming Soon →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-neutral-200 mt-16">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="flex items-center gap-3">
              <div className="h-7 w-7 rounded-xl bg-gradient-to-br from-brand-primary to-brand-primary-dark grid place-items-center text-white font-bold shadow">
                ₹
              </div>
              <span className="text-sm text-neutral-600">© {new Date().getFullYear()} paisa.io</span>
            </div>
            <div className="flex flex-wrap gap-6 text-sm text-neutral-600">
              <button className="hover:text-neutral-900 bg-transparent border-none p-0 cursor-pointer text-sm">
                Privacy Policy
              </button>
              <Link to="/terms" className="hover:text-neutral-900">
                Terms of Use
              </Link>
              <a href="mailto:paisafsggh@rpt.ai" className="hover:text-neutral-900">
                Contact
              </a>
            </div>
            <div className="text-sm text-neutral-500">Parking Page. Made with ❤️ in India</div>
          </div>
        </div>
      </footer>
    </Layout>
  );
}

export default EMICalculator;
